import React, { useEffect, useRef, useState } from 'react';
import {
  Meeting,
  CraftBlock,
  ActionItem
} from '../types';
import {
  Sparkles,
  ListChecks,
  Calendar,
  Clock,
  Users,
  Plus,
  FileText,
  Mail,
  Copy,
  Quote,
  Edit3,
  Volume2,
  Check,
  ArrowLeft,
  ChevronLeft,
  Lightbulb,
  List,
  Gavel,
  Loader2,
  MoreHorizontal,
  Pencil,
  Share2,
  ShieldCheck,
  Trash2,
  Flag
} from 'lucide-react';
import { TranscriptPlayer } from './TranscriptPlayer';
import {
  displaySummaryText,
  isGenericMeetingTitle,
  MIN_SUMMARY_SECONDS,
  shortRecordingMessage,
} from '../features/recording/summaryRules';
import { useMeetingMedia } from '../features/meetings/useMeetingMedia';
import { BotStatusBadge } from '../features/bot/BotStatusBadge';
import { isBotActive } from '../features/bot/botStatus';
import { CraftBlockEditor } from './blocks/CraftBlockEditor';
import { DeleteMeetingDialog } from '../features/meetings/DeleteMeetingDialog';
import { SupportAccessDialog } from '../features/meetings/SupportAccessDialog';
import { ReportAiDialog, type AiReportTarget } from '../features/aiReport/ReportAiDialog';
import type { SummaryEmailResult } from '../global';
import { t } from '../lib/i18n';

interface CraftNoteViewProps {
  meeting: Meeting;
  onUpdateMeeting: (updated: Meeting) => void;
  isDarkMode: boolean;
  onBackToHome: () => void;
  /** Özeti elle gönderir — otomatik gönderim kapalı olsa bile. */
  onSendSummaryEmail: (meeting: Meeting) => Promise<SummaryEmailResult>;
  /** Paylaşım penceresini açar; yoksa (masaüstü) menüde görünmez. */
  onShare?: () => void;
  /** Toplantıyı çöp kutusuna taşır (onaydan sonra). */
  onDeleteMeeting: (meeting: Meeting) => void;
  /**
   * Telefonda geri düğmesi simge değil "‹ Toplantılar" gibi etiketli; e-posta
   * ve yeniden özetleme "Diğer eylemler" menüsünde kalıyor.
   */
  mobileBackLabel?: string;
}

type TabId = 'summary' | 'transcript' | 'notes';

/**
 * İçerik sütunu. Not sayfasının başlığı, sekmeleri ve gövdesi aynı genişlikte
 * hizalanır. Eskiden `max-w-3xl` (768px) idi ve geniş pencerede metin dar bir
 * şeride sıkışıyordu; sütun artık pencereyle birlikte 1180px'e kadar açılıyor.
 */
const CONTENT_COL = 'w-full max-w-[1180px] mx-auto px-10';

/** Uygulamanın koyduğu etiketler seçili dilde görünsün; kullanıcının etiketlerine dokunulmaz. */
const SYSTEM_TAGS = [
  { tr: 'Kayıt', en: 'Recording' },
  { tr: 'Not', en: 'Note' },
];

function displayTag(tag: string): string {
  const match = SYSTEM_TAGS.find((entry) => entry.tr === tag || entry.en === tag);
  return match ? t(match.tr, match.en) : tag;
}

/** Üst baştaki ikon düğmesi — hepsi aynı ölçüde kare, aynı hizada. */
const IconButton: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  title: string;
  isDarkMode: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, title, isDarkMode, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    className={`w-9 h-9 flex items-center justify-center rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
      isDarkMode
        ? 'bg-[#19191C] text-neutral-400 enabled:hover:bg-[#1F1F23] enabled:hover:text-[#F0EEE6]'
        : 'bg-neutral-100 text-neutral-600 enabled:hover:bg-neutral-200 enabled:hover:text-neutral-900'
    }`}
  >
    {children}
  </button>
);

/** Sekme satırının sağındaki hafif ikonlar (düzenle / kopyala). */
const GhostIconButton: React.FC<{
  onClick: () => void;
  title: string;
  isDarkMode: boolean;
  children: React.ReactNode;
}> = ({ onClick, title, isDarkMode, children }) => (
  <button
    onClick={onClick}
    title={title}
    aria-label={title}
    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
      isDarkMode
        ? 'text-neutral-500 hover:text-[#F0EEE6] hover:bg-[#1F1F23]'
        : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100'
    }`}
  >
    {children}
  </button>
);

export const CraftNoteView: React.FC<CraftNoteViewProps> = ({
  meeting,
  onUpdateMeeting,
  isDarkMode,
  onBackToHome,
  onSendSummaryEmail,
  onShare,
  onDeleteMeeting,
  mobileBackLabel
}) => {
  const isMobile = mobileBackLabel !== undefined;
  const [activeTab, setActiveTab] = useState<TabId>('summary');
  const [newTodoText, setNewTodoText] = useState('');
  // Atanan kişi seçici henüz yok — ilk katılımcı varsayılan. (Seçici
  // eklenirse burası state'e döner.)
  const newTodoAssignee = meeting.participants[0]?.name || t('Ben', 'Me');
  const [slashMenuOpen, setSlashMenuOpen] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [emailState, setEmailState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [isResummarizing, setIsResummarizing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Başlık artık sürekli bir metin kutusu değil: resimdeki gibi düz metin
  // olarak duruyor, yanındaki kalem (ya da çift tıklama) düzenlemeyi açıyor.
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState<AiReportTarget | null>(null);
  // Destek erişimi yalnızca sunucuya kaydedilmiş toplantıda (kimliği UUID) açılabilir.
  const isSaved = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(meeting.id);
  const moreMenuRef = useRef<HTMLDivElement | null>(null);

  // Menü dışına tıklanınca kapansın — açık kalan bir menü altındaki içeriği örtüyor.
  useEffect(() => {
    if (!moreMenuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!moreMenuRef.current?.contains(e.target as Node)) setMoreMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [moreMenuOpen]);

  // Transkript ve ses: yerel toplantıda listeden, S3'tekinde açılışta S3'ten.
  const media = useMeetingMedia(meeting);
  const hasTranscript = media.transcript.length > 0;
  const [isStoppingBot, setIsStoppingBot] = useState(false);

  const handleStopBot = async () => {
    setIsStoppingBot(true);
    setActionError(null);
    try {
      await window.zabit.stopBot(meeting.id);
    } catch (err) {
      setActionError((err as Error).message || t('Bot durdurulamadı.', 'Could not stop the bot.'));
    } finally {
      setIsStoppingBot(false);
    }
  };

  // Toggle todo item
  const handleToggleTodo = (id: string) => {
    const updatedActionItems = meeting.actionItems.map(item =>
      item.id === id ? { ...item, completed: !item.completed } : item
    );
    onUpdateMeeting({
      ...meeting,
      actionItems: updatedActionItems
    });
  };

  // Add new todo
  const handleAddTodo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTodoText.trim()) return;

    const newItem: ActionItem = {
      id: `act-${Date.now()}`,
      text: newTodoText.trim(),
      assignee: newTodoAssignee,
      completed: false,
      dueDate: t('Bugün', 'Today')
    };

    onUpdateMeeting({
      ...meeting,
      actionItems: [...meeting.actionItems, newItem]
    });
    setNewTodoText('');
  };

  // Update craft block content
  const handleBlockChange = (id: string, newContent: string) => {
    const updatedBlocks = meeting.craftBlocks.map(b =>
      b.id === id ? { ...b, content: newContent } : b
    );
    onUpdateMeeting({
      ...meeting,
      craftBlocks: updatedBlocks
    });
  };

  // Yapılacak (todo) tipi blokların işaretini değiştir
  const handleToggleBlockChecked = (id: string) => {
    onUpdateMeeting({
      ...meeting,
      craftBlocks: meeting.craftBlocks.map(b =>
        b.id === id ? { ...b, checked: !b.checked } : b
      )
    });
  };

  // Delete craft block
  const handleDeleteBlock = (id: string) => {
    onUpdateMeeting({
      ...meeting,
      craftBlocks: meeting.craftBlocks.filter(b => b.id !== id)
    });
  };

  // Insert quote from transcript into craft note
  const handleInsertQuoteToNote = (speaker: string, text: string, timestamp: string) => {
    const newBlock: CraftBlock = {
      id: `quote-${Date.now()}`,
      type: 'quote',
      content: `"${text}" — ${speaker} (${timestamp})`
    };

    onUpdateMeeting({
      ...meeting,
      craftBlocks: [...meeting.craftBlocks, newBlock]
    });
    setActiveTab('notes');
  };

  // Add block via slash menu
  const handleAddBlock = (type: CraftBlock['type'], defaultText: string) => {
    const newBlock: CraftBlock = {
      id: `block-${Date.now()}`,
      type,
      content: defaultText
    };
    onUpdateMeeting({
      ...meeting,
      craftBlocks: [...meeting.craftBlocks, newBlock]
    });
    setSlashMenuOpen(false);
  };

  // Blok tipini Markdown karşılığına çevir. Önceki sürüm yalnızca
  // `b.content` yazıyordu — başlıklar, maddeler ve yapılacaklar düz metne
  // dönüşüp tüm yapı kayboluyordu.
  const blockToMarkdown = (block: CraftBlock): string => {
    switch (block.type) {
      case 'heading': return `## ${block.content}`;
      case 'subheading': return `### ${block.content}`;
      case 'bullet': return `- ${block.content}`;
      case 'todo': return `- [${block.checked ? 'x' : ' '}] ${block.content}`;
      case 'quote': return `> ${block.content}`;
      case 'callout': return `> **${t('Not', 'Note')}:** ${block.content}`;
      case 'decision': return `> **${block.tag || t('Karar', 'Decision')}:** ${block.content}`;
      case 'paragraph': return block.content;
      default: return block.content;
    }
  };

  // Copy full note as Markdown
  const handleCopyMarkdown = () => {
    const participants = meeting.participants.map(p => p.name).join(', ');
    const md = [
      `# ${meeting.title}`,
      '',
      `**${t('Tarih', 'Date')}:** ${meeting.date} ${meeting.time}`,
      ...(participants ? [`**${t('Katılımcılar', 'Participants')}:** ${participants}`] : []),
      '',
      t('## Yönetici Özeti (TL;DR)', '## Executive summary (TL;DR)'),
      displaySummaryText(meeting.summary.tldr),
      ...(meeting.summary.keyPoints.length
        ? ['', t('## Kilit Başlıklar', '## Key points'), ...meeting.summary.keyPoints.map(k => `- ${k}`)]
        : []),
      ...(meeting.summary.decisions.length
        ? ['', t('## Kararlar', '## Decisions'), ...meeting.summary.decisions.map(d => `- ${d}`)]
        : []),
      ...(meeting.actionItems.length
        ? ['', t('## Aksiyon Maddeleri', '## Action items'),
           ...meeting.actionItems.map(a => `- [${a.completed ? 'x' : ' '}] ${a.text} (${a.assignee})`)]
        : []),
      ...(meeting.craftBlocks.length
        ? ['', t('## Notlar & Bloklar', '## Notes & blocks'), meeting.craftBlocks.map(blockToMarkdown).join('\n\n')]
        : []),
    ].join('\n');

    navigator.clipboard.writeText(md).then(() => {
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    }, (err) => console.error('[clipboard] kopyalanamadı:', err));
  };

  /** Yapay zekânın ürettiği kısım (özet, kararlar, aksiyonlar) — "Özeti bildir"de gönderilebilir. */
  const summaryText = () =>
    [
      displaySummaryText(meeting.summary.tldr),
      ...meeting.summary.keyPoints.map((point) => `- ${point}`),
      ...meeting.summary.decisions.map((decision) => `- ${t('Karar', 'Decision')}: ${decision}`),
      ...meeting.actionItems.map((item) => `- ${t('Aksiyon', 'Action')}: ${item.text} (${item.assignee})`),
    ]
      .filter(Boolean)
      .join('\n');

  /** Özeti elle gönder — otomatik gönderim kapalıysa bile. */
  const handleSendEmail = async () => {
    if (emailState === 'sending') return;
    setEmailState('sending');
    setActionError(null);

    const result = await onSendSummaryEmail(meeting);
    if (result.ok) {
      setEmailState('sent');
      setTimeout(() => setEmailState('idle'), 2500);
    } else {
      setEmailState('idle');
      setActionError(result.message);
    }
  };

  /**
   * Özeti yeniden ürettirir. Bu düğme daha önce "Yakında" etiketiyle devre
   * dışıydı; oysa gereken her şey (transkript + `summarizeMeeting` ucu)
   * zaten vardı.
   */
  const handleResummarize = async () => {
    if (!hasTranscript || isResummarizing) return;
    // Kısa kayıtlar yapay zekâya özetletilmiyor (bkz. summaryRules).
    if (meeting.audioDurationSeconds > 0 && meeting.audioDurationSeconds < MIN_SUMMARY_SECONDS) {
      setActionError(shortRecordingMessage());
      return;
    }
    setIsResummarizing(true);
    setActionError(null);
    try {
      const generated = await window.zabit.summarizeMeeting(
        media.transcript,
        isGenericMeetingTitle(meeting.title) ? '' : meeting.title
      );
      onUpdateMeeting({
        ...meeting,
        // Başlığı kullanıcı vermediyse ("Yeni Kayıt") yapay zekânın başlığı gelsin.
        title: isGenericMeetingTitle(meeting.title) && generated.title ? generated.title : meeting.title,
        summary: {
          tldr: generated.tldr || meeting.summary.tldr,
          keyPoints: generated.keyPoints,
          decisions: generated.decisions,
        },
        // Kullanıcının işaretlediği maddeler korunuyor; yalnızca yeni
        // çıkanlar ekleniyor — yeniden özetleme bir ilerlemeyi silmemeli.
        actionItems: [
          ...meeting.actionItems,
          ...generated.actionItems
            .filter((item) => !meeting.actionItems.some((existing) => existing.text === item.text))
            .map((item, index) => ({
              id: `act-${Date.now()}-${index}`,
              text: item.text,
              assignee: item.assignee || t('Ben', 'Me'),
              completed: false,
            })),
        ],
      });
    } catch (err) {
      setActionError((err as Error).message || t('Özet yenilenemedi.', 'Could not regenerate the summary.'));
    } finally {
      setIsResummarizing(false);
    }
  };

  const tabs: { id: TabId; label: string; icon: typeof FileText }[] = [
    { id: 'summary', label: t('Özet', 'Summary'), icon: FileText },
    { id: 'transcript', label: t('Transkript', 'Transcript'), icon: Volume2 },
    { id: 'notes', label: t('Notlarım', 'My notes'), icon: Edit3 }
  ];

  const participantNames = meeting.participants.map(p => p.name).filter(Boolean);

  return (
    <div className={`h-full flex flex-col overflow-hidden select-none ${
      isDarkMode ? 'bg-[#111113] text-neutral-200' : 'bg-white text-neutral-900'
    }`}>
      {/* Üst bar — resimdeki gibi: solda geri, sağda ikon düğmeleri.
          Eskiden tam genişlikte, alt çizgiyle ayrılmış, etiketli bir araç
          çubuğuydu; başlığı aşağı itip sayfanın üstünü daraltıyordu. */}
      <div className={`shrink-0 flex items-center justify-between gap-3 ${isMobile ? 'pl-1.5 pr-4 pt-2 pb-1' : 'px-4 pt-3.5 pb-1'}`}>
        {isMobile ? (
          <button
            onClick={onBackToHome}
            data-native-back
            className="h-11 flex items-center gap-0.5 pr-2 text-[16px] font-medium text-brand-text active:opacity-70 min-w-0"
          >
            <ChevronLeft className="w-6 h-6 shrink-0" strokeWidth={2} />
            <span className="truncate">{mobileBackLabel}</span>
          </button>
        ) : (
          <IconButton onClick={onBackToHome} title={t('Geri', 'Back')} isDarkMode={isDarkMode}>
            <ArrowLeft className="w-4 h-4" />
          </IconButton>
        )}

        <div className="flex items-center gap-1.5">
          {!isMobile && (
          <IconButton
            onClick={() => void handleSendEmail()}
            disabled={emailState === 'sending'}
            title={emailState === 'sent' ? t('Özet gönderildi', 'Summary sent') : t('Özeti e-posta olarak gönder', 'Email the summary')}
            isDarkMode={isDarkMode}
          >
            {emailState === 'sending' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : emailState === 'sent' ? (
              <Check className="w-4 h-4 text-emerald-400" />
            ) : (
              <Mail className="w-4 h-4" />
            )}
          </IconButton>
          )}

          {!isMobile && onShare && (
          <IconButton onClick={onShare} disabled={!isSaved} title={t('Paylaş', 'Share')} isDarkMode={isDarkMode}>
            <Share2 className="w-4 h-4" />
          </IconButton>
          )}

          {!isMobile && (
          <IconButton
            onClick={() => void handleResummarize()}
            disabled={!hasTranscript || isResummarizing}
            title={hasTranscript ? t('Transkriptten özeti yeniden üret', 'Regenerate the summary from the transcript') : t('Bu toplantıda transkript yok', 'This meeting has no transcript')}
            isDarkMode={isDarkMode}
          >
            {isResummarizing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
          </IconButton>
          )}

          {/* Sil — kalıcı değil: çöp kutusuna gider, 30 gün geri alınabilir. */}
          <button
            onClick={() => setIsDeleteOpen(true)}
            title={t('Toplantıyı sil (çöp kutusuna taşı)', 'Delete meeting (move to trash)')}
            aria-label={t('Toplantıyı sil', 'Delete meeting')}
            className={`w-9 h-9 flex items-center justify-center rounded-xl transition-colors ${
              isDarkMode
                ? 'bg-[#19191C] text-neutral-400 hover:bg-rose-500/15 hover:text-rose-400'
                : 'bg-neutral-100 text-neutral-600 hover:bg-rose-50 hover:text-rose-600'
            }`}
          >
            <Trash2 className="w-4 h-4" />
          </button>

          {/* Etiketli karşılıkları burada duruyor — ikonların ne yaptığı
              tahmine kalmasın diye. */}
          <div className="relative" ref={moreMenuRef}>
            <IconButton
              onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              title={t('Diğer eylemler', 'More actions')}
              isDarkMode={isDarkMode}
            >
              <MoreHorizontal className="w-4 h-4" />
            </IconButton>

            {moreMenuOpen && (
              <div className={`absolute right-0 mt-1.5 w-64 p-1.5 rounded-2xl border shadow-xl z-40 space-y-0.5 text-[13px] ${
                isDarkMode ? 'bg-[#19191C] border-[#45454B] text-neutral-200' : 'bg-white border-neutral-200 text-neutral-800'
              }`}>
                {([
                  ...(onShare
                    ? [{
                        key: 'share',
                        label: t('Paylaş…', 'Share…'),
                        icon: <Share2 className="w-4 h-4 text-[#46CBE2]" />,
                        disabled: !isSaved,
                        run: onShare,
                      }]
                    : []),
                  {
                    key: 'copy',
                    label: copyFeedback ? t('Kopyalandı', 'Copied') : t('Markdown olarak kopyala', 'Copy as Markdown'),
                    icon: copyFeedback ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4 text-neutral-400" />,
                    disabled: false,
                    run: handleCopyMarkdown,
                  },
                  {
                    key: 'resummarize',
                    label: isResummarizing ? t('Özetleniyor…', 'Summarizing…') : t('Özeti yeniden üret', 'Regenerate summary'),
                    icon: <Sparkles className="w-4 h-4 text-[#46CBE2]" />,
                    disabled: !hasTranscript || isResummarizing,
                    run: () => void handleResummarize(),
                  },
                  {
                    key: 'email',
                    label: emailState === 'sent' ? t('Gönderildi', 'Sent') : t('Özeti e-posta gönder', 'Email summary'),
                    icon: <Mail className="w-4 h-4 text-amber-400" />,
                    disabled: emailState === 'sending',
                    run: () => void handleSendEmail(),
                  },
                  {
                    key: 'support',
                    label: t('Destek erişimi…', 'Support access…'),
                    icon: <ShieldCheck className="w-4 h-4 text-emerald-400" />,
                    disabled: !isSaved,
                    run: () => setIsSupportOpen(true),
                  },
                  // Yapay zekâ içeriğini bildir (Google Play yapay zekâ içeriği politikası).
                  {
                    key: 'report',
                    label: t('Özeti bildir…', 'Report summary…'),
                    icon: <Flag className="w-4 h-4 text-rose-400" />,
                    disabled: !summaryText(),
                    run: () => setReportTarget({ surface: 'summary', content: summaryText(), meetingId: isSaved ? meeting.id : undefined }),
                  },
                ]).map((entry) => (
                  <button
                    key={entry.key}
                    disabled={entry.disabled}
                    onClick={() => {
                      entry.run();
                      setMoreMenuOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                      isDarkMode ? 'enabled:hover:bg-neutral-700/40' : 'enabled:hover:bg-neutral-100'
                    }`}
                  >
                    {entry.icon}
                    <span>{entry.label}</span>
                  </button>
                ))}
                <div className={`my-1 h-px ${isDarkMode ? 'bg-[#45454B]' : 'bg-neutral-200'}`} />
                <button
                  onClick={() => {
                    setMoreMenuOpen(false);
                    setIsDeleteOpen(true);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2.5 transition-colors text-rose-400 ${
                    isDarkMode ? 'hover:bg-rose-500/10' : 'hover:bg-rose-50 text-rose-600'
                  }`}
                >
                  <Trash2 className="w-4 h-4" />
                  <span>{t('Toplantıyı sil', 'Delete meeting')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {isSaved && (
        <SupportAccessDialog open={isSupportOpen} meetingId={meeting.id} onClose={() => setIsSupportOpen(false)} />
      )}
      <ReportAiDialog target={reportTarget} onClose={() => setReportTarget(null)} />

      <DeleteMeetingDialog
        open={isDeleteOpen}
        title={meeting.title}
        hasActiveBot={Boolean(meeting.bot && isBotActive(meeting.bot))}
        onCancel={() => setIsDeleteOpen(false)}
        onConfirm={() => {
          setIsDeleteOpen(false);
          onDeleteMeeting(meeting);
        }}
      />

      {actionError && (
        <div className={`shrink-0 ${CONTENT_COL} pt-2`}>
          <div className="text-[12.5px] text-rose-300 bg-rose-500/10 border border-rose-500/25 rounded-lg px-3 py-2">
            {actionError}
          </div>
        </div>
      )}

      {/* Başlık + künye — sekmeler ne olursa olsun sabit kalır */}
      <div className={`${CONTENT_COL} pt-6 shrink-0`}>
        <div className="flex items-center gap-2 min-w-0">
          {isEditingTitle ? (
            <input
              type="text"
              autoFocus
              value={meeting.title}
              onChange={(e) => onUpdateMeeting({ ...meeting, title: e.target.value })}
              onBlur={() => setIsEditingTitle(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') setIsEditingTitle(false);
              }}
              className={`text-[26px] md:text-[32px] leading-tight font-bold bg-transparent border-none outline-none w-full tracking-tight placeholder:text-neutral-600 ${
                isDarkMode ? 'text-[#F0EEE6]' : 'text-neutral-900'
              }`}
              placeholder={t('Toplantı Başlığı...', 'Meeting title…')}
            />
          ) : (
            <>
              <h1
                onDoubleClick={() => setIsEditingTitle(true)}
                // Dar ekranda (telefon) başlık kesilmesin, alt satıra kaysın.
                className={`text-[26px] md:text-[32px] leading-tight font-bold tracking-tight line-clamp-3 md:line-clamp-none md:truncate min-w-0 ${
                  isDarkMode ? 'text-[#F0EEE6]' : 'text-neutral-900'
                } ${meeting.title ? '' : 'text-neutral-600'}`}
              >
                {meeting.title || t('Toplantı Başlığı...', 'Meeting title…')}
              </h1>
              <button
                onClick={() => setIsEditingTitle(true)}
                title={t('Başlığı düzenle', 'Edit title')}
                aria-label={t('Başlığı düzenle', 'Edit title')}
                className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
                  isDarkMode
                    ? 'text-neutral-500 hover:text-[#F0EEE6] hover:bg-[#1F1F23]'
                    : 'text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100'
                }`}
              >
                <Pencil className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        <div className={`flex flex-wrap items-center gap-x-2.5 gap-y-2 text-[13px] mt-2.5 ${
          isDarkMode ? 'text-neutral-400' : 'text-neutral-600'
        }`}>
          <span className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-amber-400" />
            <span>{meeting.duration}</span>
          </span>
          <span className="text-neutral-600">·</span>
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-[#46CBE2]" />
            <span>{meeting.date}</span>
          </span>

          {/* Saat aralığı eskiden üst araç çubuğundaydı; araç çubuğu ikonlara
              indiği için künyeye taşındı — kaybolmadı. */}
          {meeting.timeRange && (
            <>
              <span className="text-neutral-600">·</span>
              <span className="tabular-nums">{meeting.timeRange}</span>
            </>
          )}

          {/* Katılımcı yoksa rozet tamamen gizleniyor — eskiden boş bir metin
              ve öksüz bir "·" kalıyordu. */}
          {participantNames.length > 0 && (
            <span
              title={participantNames.join(', ')}
              className={`flex items-center gap-1.5 min-w-0 max-w-[340px] rounded-full border px-2.5 py-1 ${
                isDarkMode ? 'border-[#3C3C42] bg-[#19191C]' : 'border-neutral-200 bg-neutral-50'
              }`}
            >
              <Users className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <span className="truncate">{participantNames.join(', ')}</span>
            </span>
          )}

          {meeting.bot && (
            <span className="flex items-center gap-2">
              <BotStatusBadge bot={meeting.bot} />
              {isBotActive(meeting.bot) && (
                <button
                  onClick={() => void handleStopBot()}
                  disabled={isStoppingBot}
                  className="text-[12px] font-semibold text-rose-400 hover:text-rose-300 disabled:opacity-50 transition-colors"
                >
                  {isStoppingBot ? t('Durduruluyor…', 'Stopping…') : t('Botu çıkar', 'Remove bot')}
                </button>
              )}
              {meeting.bot.error && (
                <span className="text-[12px] text-rose-400">{meeting.bot.error}</span>
              )}
              {meeting.bot.status === 'waiting_to_record' && /meet\.google\.com/i.test(meeting.bot.meetingUrl ?? '') && (
                <span className="text-[12px] text-amber-400">
                  {t(
                    'Toplantıda botu kabul et — Google Meet onu “potansiyel risk” altında gösterebilir.',
                    'Admit the bot in the meeting — Google Meet may list it under “potential risk”.'
                  )}
                </span>
              )}
            </span>
          )}

          {meeting.tags.length > 0 && (
            <span className="flex items-center gap-2">
              {meeting.tags.map((tag, idx) => (
                <span key={idx} className="text-[#46CBE2] font-medium">#{displayTag(tag)}</span>
              ))}
            </span>
          )}
        </div>
      </div>

      {/* Sekmeler — sağ uçta düzenle & kopyala, resimdeki yerleşim */}
      <div className={`${CONTENT_COL} mt-6 shrink-0`}>
        <div className={`flex items-center justify-between gap-4 border-b ${
          isDarkMode ? 'border-[#38383C]' : 'border-neutral-200'
        }`}>
          <div className="flex items-center gap-1 min-w-0">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-2.5 sm:px-3 py-2.5 -mb-px border-b-2 text-[13.5px] font-semibold transition-colors ${
                    active
                      ? 'border-[#3AC8E0] text-[#46CBE2]'
                      : `border-transparent ${isDarkMode ? 'text-neutral-500 hover:text-neutral-300' : 'text-neutral-500 hover:text-neutral-800'}`
                  }`}
                >
                  {/* Telefonda (web) simgeler yer kaplamasın — sekmeler tek satıra sığsın. */}
                  <Icon className="w-4 h-4 hidden sm:block" />
                  <span>{tab.label}</span>
                  {tab.id === 'notes' && meeting.craftBlocks.length > 0 && (
                    <span className={`text-[11px] font-mono px-1.5 py-0.5 rounded-full ${
                      active
                        ? 'bg-[#1DA4BA]/15 text-[#46CBE2]'
                        : isDarkMode ? 'bg-[#19191C] text-neutral-500' : 'bg-neutral-100 text-neutral-500'
                    }`}>
                      {meeting.craftBlocks.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-0.5 shrink-0 pb-1.5">
            <span className="hidden sm:inline-flex">
              <GhostIconButton
                onClick={() => setActiveTab('notes')}
                title={t('Notlarımı düzenle', 'Edit my notes')}
                isDarkMode={isDarkMode}
              >
                <Edit3 className="w-4 h-4" />
              </GhostIconButton>
            </span>
            <GhostIconButton
              onClick={handleCopyMarkdown}
              title={t('Notun tamamını Markdown olarak kopyala', 'Copy the whole note as Markdown')}
              isDarkMode={isDarkMode}
            >
              {copyFeedback ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            </GhostIconButton>
          </div>
        </div>
      </div>

      {/* Sekme içeriği */}
      <div className="flex-1 min-h-0">
        {activeTab === 'summary' && (
          // Görünümün kökü `select-none`; özet metni fareyle seçilip
          // kopyalanabilsin diye burada yeniden açılıyor.
          <div className="h-full overflow-y-auto select-text">
            <div className={`${CONTENT_COL} py-8 space-y-8`}>
              {/* TL;DR Executive Summary — kutu yerine tek bir vurgu çizgisi */}
              <div className="pl-5 border-l-2 border-[#3AC8E0]">
                <div className="flex items-center gap-2 mb-2 text-[11px] font-bold uppercase tracking-[0.09em] text-[#46CBE2]">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>{t('Yönetici Özeti', 'Executive summary')}</span>
                </div>
                <p className={`text-[15px] leading-relaxed ${
                  isDarkMode ? 'text-neutral-300' : 'text-neutral-700'
                }`}>
                  {displaySummaryText(meeting.summary.tldr)}
                </p>
                <p className="mt-2.5 text-[11.5px] leading-snug text-neutral-500">
                  {t(
                    'Özet, kararlar ve aksiyonlar yapay zekâ ile otomatik hazırlandı; hata ya da eksik içerebilir. Önemli konularda kayıtla karşılaştır.',
                    'The summary, decisions and actions were generated automatically by AI and may contain errors or omissions. Check important points against the recording.'
                  )}
                </p>
              </div>

              {/* Key Points (Kilit Başlıklar) */}
              {meeting.summary.keyPoints.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-[11px] font-bold tracking-[0.09em] uppercase text-neutral-500">
                    {t('Kilit Başlıklar & Çıkarımlar', 'Key points & takeaways')}
                  </h3>
                  <div className="space-y-2">
                    {meeting.summary.keyPoints.map((point, idx) => (
                      <div
                        key={idx}
                        className={`flex items-start gap-3 text-[14px] leading-relaxed ${
                          isDarkMode ? 'text-neutral-300' : 'text-neutral-700'
                        }`}
                      >
                        <span className="w-1 h-1 rounded-full bg-[#3AC8E0] shrink-0 mt-2.5"></span>
                        <span className="flex-1">{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Kararlar — daha önce yalnızca not bloklarına gömülüyordu,
                  özet sekmesinde hiç görünmüyordu. */}
              {meeting.summary.decisions.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-[11px] font-bold tracking-[0.09em] uppercase text-neutral-500 flex items-center gap-2">
                    <Gavel className="w-3.5 h-3.5 text-amber-400" />
                    {t('Kararlar', 'Decisions')}
                  </h3>
                  <div className="space-y-2">
                    {meeting.summary.decisions.map((decision, idx) => (
                      <div
                        key={idx}
                        className={`flex items-start gap-3 text-[14px] leading-relaxed ${
                          isDarkMode ? 'text-neutral-300' : 'text-neutral-700'
                        }`}
                      >
                        <span className="w-1 h-1 rounded-full bg-amber-400 shrink-0 mt-2.5"></span>
                        <span className="flex-1">{decision}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Items (Interactive Todos) */}
              <div className="space-y-3">
                <h3 className="text-[11px] font-bold tracking-[0.09em] uppercase text-neutral-500 flex items-center gap-2">
                  <ListChecks className="w-3.5 h-3.5 text-emerald-400" />
                  <span>
                    {t('Aksiyon Maddeleri', 'Action items')}
                    {meeting.actionItems.length > 0 && (
                      <span className="ml-1.5 font-mono text-neutral-600">
                        {meeting.actionItems.filter(a => a.completed).length}/{meeting.actionItems.length}
                      </span>
                    )}
                  </span>
                </h3>

                <div className={`divide-y ${isDarkMode ? 'divide-[#343438]' : 'divide-neutral-100'}`}>
                  {meeting.actionItems.map((todo) => (
                    <div
                      key={todo.id}
                      className={`py-2.5 flex items-center justify-between gap-3.5 transition-opacity ${
                        todo.completed ? 'opacity-50' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <button
                          onClick={() => handleToggleTodo(todo.id)}
                          aria-label={todo.completed ? t('Tamamlandı işaretini kaldır', 'Mark as not done') : t('Tamamlandı olarak işaretle', 'Mark as done')}
                          className={`w-4.5 h-4.5 rounded border flex items-center justify-center transition-colors shrink-0 ${
                            todo.completed
                              ? 'bg-emerald-500 border-emerald-500 text-[#F0EEE6]'
                              : 'border-neutral-500 hover:border-emerald-400'
                          }`}
                        >
                          {todo.completed && <Check className="w-3 h-3 stroke-[3]" />}
                        </button>
                        <span className={`text-[14px] truncate ${
                          todo.completed ? 'line-through text-neutral-500' : isDarkMode ? 'text-neutral-200' : 'text-neutral-800'
                        }`}>
                          {todo.text}
                        </span>
                      </div>

                      <span className="text-[12px] text-neutral-500 shrink-0">
                        {todo.assignee}
                      </span>
                    </div>
                  ))}

                  {/* Add Todo Inline Form */}
                  <form onSubmit={handleAddTodo} className="flex items-center gap-2.5 pt-3">
                    <input
                      type="text"
                      placeholder={t('Yeni aksiyon maddesi ekle…', 'Add a new action item…')}
                      value={newTodoText}
                      onChange={(e) => setNewTodoText(e.target.value)}
                      className={`flex-1 px-1 py-1.5 border-b bg-transparent text-[14px] outline-none transition-colors ${
                        isDarkMode
                          ? 'border-[#3F3F45] text-neutral-200 focus:border-[#3AC8E0] placeholder:text-neutral-600'
                          : 'border-neutral-200 text-neutral-800 focus:border-[#46CBE2] placeholder:text-neutral-400'
                      }`}
                    />
                    <button
                      type="submit"
                      disabled={!newTodoText.trim()}
                      className="px-3 py-1.5 rounded-lg bg-[#1DA4BA] hover:bg-[#3AC8E0] disabled:opacity-30 text-[#F0EEE6] text-[12.5px] font-semibold transition-colors"
                    >
                      {t('Ekle', 'Add')}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transcript' && (
          <div className={`h-full ${CONTENT_COL}`}>
            <TranscriptPlayer
              transcript={media.transcript}
              durationSeconds={meeting.audioDurationSeconds}
              isDarkMode={isDarkMode}
              onInsertQuoteToNote={handleInsertQuoteToNote}
              audioUrl={media.audioUrl}
              audioFilePath={meeting.storage === 'local' ? meeting.audioFilePath : undefined}
              isLoadingMedia={media.isLoading}
              mediaError={media.error}
              onAudioError={media.refreshAudio}
              isRemote={meeting.storage === 's3'}
              speakerMap={meeting.speakerMap}
              onAssignSpeaker={
                meeting.id.startsWith('temp-')
                  ? undefined
                  : (speaker, personId) => window.zabit.assignSpeaker(meeting.id, speaker, personId)
              }
            />
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="h-full overflow-y-auto">
            <div className={`${CONTENT_COL} py-8 space-y-3`}>
              {meeting.craftBlocks.length === 0 && (
                <p className="text-[13.5px] text-neutral-500 leading-relaxed">
                  {t(
                    'Henüz not yok — aşağıdan bir blok ekleyebilir ya da Transkript sekmesinden alıntı taşıyabilirsiniz.',
                    'No notes yet — add a block below or bring in quotes from the Transcript tab.'
                  )}
                </p>
              )}

              {meeting.craftBlocks.map((block) => (
                <CraftBlockEditor
                  key={block.id}
                  block={block}
                  isDarkMode={isDarkMode}
                  onChange={handleBlockChange}
                  onToggleChecked={handleToggleBlockChecked}
                  onDelete={handleDeleteBlock}
                />
              ))}

              {/* Quick Add Block & Slash Action Toolbar */}
              <div className="pt-2.5 relative">
                <button
                  onClick={() => setSlashMenuOpen(!slashMenuOpen)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13.5px] font-semibold transition-all ${
                    isDarkMode
                      ? 'text-neutral-400 hover:text-[#F0EEE6] hover:bg-[#1F1F23]'
                      : 'text-neutral-600 hover:text-black hover:bg-neutral-100'
                  }`}
                >
                  <Plus className="w-4 h-4 text-[#46CBE2]" />
                  <span>{t('Blok Ekle', 'Add block')}</span>
                </button>

                {slashMenuOpen && (
                  <div className={`mt-2 p-2 rounded-2xl border shadow-xl z-20 max-w-xs space-y-0.5 text-[13.5px] ${
                    isDarkMode ? 'bg-[#19191C] border-[#47474C] text-neutral-200' : 'bg-white border-neutral-200 text-neutral-800'
                  }`}>
                    {([
                      { type: 'paragraph', text: t('Yeni paragraf metni...', 'New paragraph…'), label: t('Paragraf Metni', 'Paragraph'), icon: <FileText className="w-4 h-4 text-neutral-400" /> },
                      { type: 'heading', text: t('Yeni Bölüm Başlığı', 'New section heading'), label: t('Bölüm Başlığı', 'Section heading'), icon: <span className="font-bold text-[14px] text-[#46CBE2] w-4 text-center">H1</span> },
                      { type: 'subheading', text: t('Alt Başlık', 'Subheading'), label: t('Alt Başlık', 'Subheading'), icon: <span className="font-bold text-[12px] text-[#46CBE2] w-4 text-center">H2</span> },
                      { type: 'bullet', text: t('Yeni madde', 'New bullet'), label: t('Madde İşareti', 'Bullet'), icon: <List className="w-4 h-4 text-neutral-400" /> },
                      { type: 'todo', text: t('Yeni yapılacak', 'New to-do'), label: t('Yapılacak Maddesi', 'To-do'), icon: <ListChecks className="w-4 h-4 text-emerald-400" /> },
                      { type: 'callout', text: t('💡 Önemli Not: ', '💡 Important note: '), label: t('Vurgu / Callout Kutusu', 'Callout'), icon: <Lightbulb className="w-4 h-4 text-amber-400" /> },
                      { type: 'decision', text: t('Alınan karar...', 'Decision made…'), label: t('Karar Bloğu', 'Decision'), icon: <Gavel className="w-4 h-4 text-amber-400" /> },
                      { type: 'quote', text: t('"Önemli bir alıntı..."', '"An important quote…"'), label: t('Alıntı Bloğu', 'Quote'), icon: <Quote className="w-4 h-4 text-purple-400" /> },
                    ] as Array<{ type: CraftBlock['type']; text: string; label: string; icon: React.ReactNode }>).map((entry) => (
                      <button
                        key={entry.type}
                        onClick={() => handleAddBlock(entry.type, entry.text)}
                        className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2.5 transition-colors ${
                          isDarkMode ? 'hover:bg-neutral-700/40' : 'hover:bg-neutral-100'
                        }`}
                      >
                        {entry.icon}
                        <span>{entry.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
